import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight, ArrowLeft, Brain, Loader2 } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { useMetaAccount } from "@/contexts/MetaAccountContext";
import { useDateRange } from "@/contexts/DateRangeContext";
import { MetricCard } from "@/components/meta/MetricCard";
import { DateRangePicker } from "@/components/meta/DateRangePicker";
import { InsightsChart } from "@/components/meta/InsightsChart";

interface Props {
  campaignId: string;
}

export default function MetaCampaignDetailPage({ campaignId }: Props) {
  const { selectedAccountId } = useMetaAccount();
  const { dateRange, formatDate } = useDateRange();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const dateFrom = formatDate(dateRange.from);
  const dateTo = formatDate(dateRange.to);

  const { data: campaign, isLoading } = useQuery({
    queryKey: ["/api/meta/accounts", selectedAccountId, "campaigns", campaignId, dateFrom, dateTo],
    queryFn: async () => {
      if (!selectedAccountId) return null;
      const res = await fetch(`/api/meta/accounts/${selectedAccountId}/campaigns/${campaignId}?dateFrom=${dateFrom}&dateTo=${dateTo}`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!selectedAccountId,
  });

  const { data: spendTimeseries = [] } = useQuery({
    queryKey: ["/api/meta/timeseries-campaign", selectedAccountId, campaignId, dateFrom, dateTo],
    queryFn: async () => {
      if (!selectedAccountId || !campaign) return [];
      const res = await fetch(`/api/meta/accounts/${selectedAccountId}/insights/timeseries?dateFrom=${dateFrom}&dateTo=${dateTo}&metric=spend&level=campaign&entityId=${campaign.campaignId}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!selectedAccountId && !!campaign,
  });

  const { data: roasTimeseries = [] } = useQuery({
    queryKey: ["/api/meta/timeseries-campaign-roas", selectedAccountId, campaignId, dateFrom, dateTo],
    queryFn: async () => {
      if (!selectedAccountId || !campaign) return [];
      const res = await fetch(`/api/meta/accounts/${selectedAccountId}/insights/timeseries?dateFrom=${dateFrom}&dateTo=${dateTo}&metric=roas&level=campaign&entityId=${campaign.campaignId}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!selectedAccountId && !!campaign,
  });

  const analyzeMutation = useMutation({
    mutationFn: async () => {
      if (!selectedAccountId || !campaign) throw new Error("No account or campaign");
      const res = await fetch(`/api/meta/accounts/${selectedAccountId}/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "health_check",
          dateFrom,
          dateTo,
          entityType: "campaign",
          entityId: campaign.campaignId,
        }),
      });
      if (!res.ok) throw new Error("Analysis failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meta/accounts", selectedAccountId, "analyses"] });
    },
  });

  const chartData = spendTimeseries.map((s: any, i: number) => ({
    date: s.date,
    spend: parseFloat(s.value),
    roas: roasTimeseries[i] ? parseFloat(roasTimeseries[i].value) : 0,
  }));

  if (isLoading) {
    return (
      <Layout>
      <div className="p-6">
        <Skeleton className="h-8 w-64 mb-6" />
        <div className="grid grid-cols-5 gap-4 mb-6">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-[300px] mb-6" />
      </div>
      </Layout>
    );
  }

  if (!campaign) {
    return (
      <Layout>
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate("/meta/campaigns")} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Campaigns
        </Button>
        <p className="text-gray-500">Campaign not found.</p>
      </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="p-6" data-testid="meta-campaign-detail-page">
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
        <button onClick={() => navigate("/meta/dashboard")} className="hover:text-indigo-500">Dashboard</button>
        <ChevronRight className="h-3 w-3" />
        <button onClick={() => navigate("/meta/campaigns")} className="hover:text-indigo-500">Campaigns</button>
        <ChevronRight className="h-3 w-3" />
        <span className="text-gray-900 font-medium">{campaign.name}</span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            {campaign.objective && (
              <Badge variant="secondary" className="text-xs bg-indigo-100 text-indigo-700">{campaign.objective}</Badge>
            )}
            {campaign.effectiveStatus && (
              <Badge variant="secondary" className={`text-xs ${campaign.effectiveStatus === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-gray-100"}`}>
                {campaign.effectiveStatus}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker />
          <Button
            className="bg-indigo-500 hover:bg-indigo-600"
            onClick={() => analyzeMutation.mutate()}
            disabled={analyzeMutation.isPending}
            data-testid="analyze-campaign-button"
          >
            {analyzeMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Brain className="h-4 w-4 mr-2" />}
            {analyzeMutation.isPending ? "Analyzing..." : "Analyze Campaign"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <MetricCard title="Spend" value={campaign.metrics?.spend || "0"} format="currency" />
        <MetricCard title="ROAS" value={campaign.metrics?.roas || "0"} format="multiplier" />
        <MetricCard title="CPA" value={campaign.metrics?.cpa || "0"} format="currency" invertColors />
        <MetricCard title="CTR" value={campaign.metrics?.ctr || "0"} format="percentage" />
        <MetricCard title="Purchases" value={campaign.metrics?.purchases || 0} format="number" />
      </div>

      <div className="mb-6">
        <InsightsChart data={chartData} title="Daily Performance" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ad Sets</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <AdsetsList accountId={selectedAccountId!} campaignId={parseInt(campaignId)} dateFrom={dateFrom} dateTo={dateTo} />
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
}

function AdsetsList({ accountId, campaignId, dateFrom, dateTo }: { accountId: number; campaignId: number; dateFrom: string; dateTo: string }) {
  const { data: adsets = [] } = useQuery({
    queryKey: ["/api/meta/accounts", accountId, "campaigns", campaignId, "adsets-detail", dateFrom, dateTo],
    queryFn: async () => {
      const res = await fetch(`/api/meta/accounts/${accountId}/campaigns/${campaignId}/adsets?dateFrom=${dateFrom}&dateTo=${dateTo}`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  const [expandedAdset, setExpandedAdset] = useState<number | null>(null);

  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-gray-50">
          <TableHead className="font-semibold">Ad Set</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Spend</TableHead>
          <TableHead>ROAS</TableHead>
          <TableHead>CPA</TableHead>
          <TableHead>CTR</TableHead>
          <TableHead>Purchases</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {adsets.length === 0 ? (
          <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">No ad sets found</TableCell></TableRow>
        ) : adsets.map((as: any) => (
          <>
            <TableRow
              key={as.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => setExpandedAdset(expandedAdset === as.id ? null : as.id)}
              data-testid={`detail-adset-row-${as.id}`}
            >
              <TableCell className="font-medium">{as.name}</TableCell>
              <TableCell><Badge variant="secondary" className="text-xs">{as.effectiveStatus || "N/A"}</Badge></TableCell>
              <TableCell className="font-mono text-sm">${parseFloat(as.metrics?.spend || "0").toFixed(2)}</TableCell>
              <TableCell className="font-mono text-sm">{parseFloat(as.metrics?.roas || "0").toFixed(2)}x</TableCell>
              <TableCell className="font-mono text-sm">${parseFloat(as.metrics?.cpa || "0").toFixed(2)}</TableCell>
              <TableCell className="font-mono text-sm">{parseFloat(as.metrics?.ctr || "0").toFixed(2)}%</TableCell>
              <TableCell className="font-mono text-sm">{as.metrics?.purchases || 0}</TableCell>
            </TableRow>
            {expandedAdset === as.id && (
              <TableRow key={`ads-${as.id}`}>
                <TableCell colSpan={7} className="p-0 bg-gray-50">
                  <AdsForAdset accountId={accountId} adsetId={as.id} dateFrom={dateFrom} dateTo={dateTo} />
                </TableCell>
              </TableRow>
            )}
          </>
        ))}
      </TableBody>
    </Table>
  );
}

function AdsForAdset({ accountId, adsetId, dateFrom, dateTo }: { accountId: number; adsetId: number; dateFrom: string; dateTo: string }) {
  const { data: ads = [] } = useQuery({
    queryKey: ["/api/meta/accounts", accountId, "adsets", adsetId, "ads-detail", dateFrom, dateTo],
    queryFn: async () => {
      const res = await fetch(`/api/meta/accounts/${accountId}/adsets/${adsetId}/ads?dateFrom=${dateFrom}&dateTo=${dateTo}`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  return (
    <div className="px-6 py-3">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-100">
            <TableHead className="text-xs">Ad</TableHead>
            <TableHead className="text-xs">Headline</TableHead>
            <TableHead className="text-xs">Spend</TableHead>
            <TableHead className="text-xs">Clicks</TableHead>
            <TableHead className="text-xs">CTR</TableHead>
            <TableHead className="text-xs">ROAS</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ads.map((ad: any) => (
            <TableRow key={ad.id} data-testid={`detail-ad-row-${ad.id}`}>
              <TableCell className="text-sm max-w-[150px] truncate">{ad.name}</TableCell>
              <TableCell className="text-sm max-w-[150px] truncate text-gray-500">{ad.headline || "—"}</TableCell>
              <TableCell className="text-sm font-mono">${parseFloat(ad.metrics?.spend || "0").toFixed(2)}</TableCell>
              <TableCell className="text-sm font-mono">{ad.metrics?.clicks || 0}</TableCell>
              <TableCell className="text-sm font-mono">{parseFloat(ad.metrics?.ctr || "0").toFixed(2)}%</TableCell>
              <TableCell className="text-sm font-mono">{parseFloat(ad.metrics?.roas || "0").toFixed(2)}x</TableCell>
            </TableRow>
          ))}
          {ads.length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-4">No ads found</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
